import 'intersection-observer';
import { useEffect, useRef, useState, useMemo } from 'react';

import { getWindowProp } from './dom';
const InteractionObserver = getWindowProp('IntersectionObserver');

export const useIntersect = ({ root = null, rootMargin, threshold = 0 }) => {
	const [entry, updateEntry] = useState({});
	const [node, setNode] = useState(null);

	const observer = useRef(
		InteractionObserver
			? new InteractionObserver(([entry]) => updateEntry(entry), {
					root,
					rootMargin,
					threshold,
			  })
			: false
	);

	useEffect(() => {
		if (!observer) return;
		const { current: currentObserver } = observer;
		currentObserver.disconnect();

		if (node) currentObserver.observe(node);

		return () => currentObserver.disconnect();
	}, [node]);

	return [setNode, entry];
};

export const usePrinting = () => {
	const [printing, setPrinting] = useState(false);

	// We make a custom function to trigger a print since
	// window.print somehow renders before the next tick
	// but File > Print does not.
	const triggerPrint = (e) => {
		e.preventDefault();
		setPrinting(true);
		setTimeout(() => {
			window.print();
			setPrinting(false);
		}, 300);
	};

	useEffect(() => {
		const beforePrint = () => setPrinting(true);
		const afterPrint = () => setPrinting(false);

		if (window.matchMedia) {
			const mediaQueryList = window.matchMedia('print');
			mediaQueryList.addListener(function (mql) {
				if (mql.matches) {
					beforePrint();
				} else {
					afterPrint();
				}
			});
		}

		window.onbeforeprint = beforePrint;
		window.onafterprint = afterPrint;
	}, []);

	return [printing, triggerPrint];
};

export const useWindowSize = () => {
	const getSize = () => ({
		width: getWindowProp('innerWidth' || 1280),
		height: getWindowProp('innerHeight' || 700),
	});
	const [windowSize, setWindowSize] = useState(getSize);
	useEffect(() => {
		if (!getWindowProp('addEventListener')) return;
		const handleResize = () => setWindowSize(getSize());

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []); // Empty array ensures that effect is only run on mount and unmount
	return windowSize;
};
